import { type RefSelectProps, Select } from 'antd'
import clsx from 'clsx'
import {
  type Ref,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react'
import toast from 'react-hot-toast'
import { FaTimes, FaTrash, FaEdit } from 'react-icons/fa'

import { Button } from '@/components/elements'
import { type SingleOrMultipleSelectProps } from '@/components/types'
import {
  DESCRIPTIONS,
  ACTIONS,
  SUCCESS_MESSAGES,
  ERROR_MESSAGES,
  CONFIRMATION_MESSAGES,
  MENU
} from '@/constants/common/message.constants'
import { SelectOptionWithAction } from '@/features/shared/components'
import { toastApiErrorMessage } from '@/features/shared/utils'
import { useAntdModal } from '@/libs/antd/hooks'
import {
  type ShipmentOrderInputHistoryDetailEntity,
  type ShipmentOrderInputHistoryListEntity
} from '@/libs/api'
import { useSelectOnChangeHandler } from '@/libs/hooks'

import { type UpdateShipmentOrderHistoryRequest } from '../../api'
import { fetchShipmentOrderHistory } from '../../api/fetch-shipment-order-history'
import {
  useShipmentOrderHistoriesSelectQuery,
  useDeleteShipmentOrderHistoryMutation
} from '../../hooks'

type Props = SingleOrMultipleSelectProps<
  number,
  ShipmentOrderInputHistoryListEntity
> & {
  tenantKey: string
  customerId?: number
  shipmentCategoryId?: number
  unselectedHistories?: ShipmentOrderInputHistoryListEntity[]
  onHistorySelect?: (history: ShipmentOrderInputHistoryDetailEntity) => void
  onEditRequest?: (request: UpdateShipmentOrderHistoryRequest) => void
}

/**
 * 配送依頼履歴を選択するSelectのベースComponent
 */
export const ShipmentOrderInputHistorySelect = forwardRef(
  (
    {
      className,
      tenantKey,
      customerId,
      shipmentCategoryId,
      unselectedHistories,
      value,
      placeholder = DESCRIPTIONS.SELECT,
      mode,
      onChange,
      onHistorySelect,
      onEditRequest,
      ...props
    }: Props,
    ref: Ref<RefSelectProps> | undefined
  ) => {
    const [modal, modalContext] = useAntdModal()
    const mutation = useDeleteShipmentOrderHistoryMutation()
    // 履歴の一覧を取得（選択されていても絞り込まない）
    const { data, isFetching, isLoading } =
      useShipmentOrderHistoriesSelectQuery(tenantKey, {
        customerId,
        shipmentCategoryId
      })

    const [isHistoryDetailLoading, setIsHistoryDetailLoading] = useState(false)
    const prevHistoryValueRef = useRef<number | null>(null)

    useEffect(() => {
      const loadHistoryDetail = async () => {
        const historyId = typeof value === 'number' && value > 0 ? value : null
        if (!historyId) {
          // 値がクリアされた場合はrefをリセットする
          prevHistoryValueRef.current = null
          return
        }

        // 前回と同じIDの場合は再取得しない
        if (historyId === prevHistoryValueRef.current) {
          return
        }

        setIsHistoryDetailLoading(true)
        try {
          const response = await fetchShipmentOrderHistory({
            tenantKey,
            id: historyId
          })
          if (onHistorySelect) {
            onHistorySelect(response.data)
            prevHistoryValueRef.current = historyId
          }
        } catch (error) {
          console.error('Failed to fetch history detail:', error)
        } finally {
          setIsHistoryDetailLoading(false)
        }
      }
      loadHistoryDetail()
    }, [tenantKey, value, onHistorySelect])

    const handleChange = useSelectOnChangeHandler({ mode, onChange })

    const handleEdit = useCallback(
      (id: number, item: ShipmentOrderInputHistoryListEntity) => {
        if (onEditRequest) {
          // nameがない場合はフォールバック値を使用
          const currentName =
            item.name || `${item.originSite} → ${item.destSite}`

          onEditRequest({
            tenantKey,
            id,
            dto: { name: currentName }
          })
        }
      },
      [onEditRequest, tenantKey]
    )

    const handleDelete = useCallback(
      (id: number, label: string) => {
        modal.confirm({
          title: MENU.DELETING_SHIPMENT_ORDER_HISTORY,
          icon: <FaTimes className="mr-2 mt-[3px] text-[18px] text-red" />,
          content: CONFIRMATION_MESSAGES.DELETE(label),
          okText: ACTIONS.DELETE,
          okButtonProps: { danger: true },
          cancelText: ACTIONS.CANCEL,
          onOk: async () => {
            try {
              await mutation.mutateAsync({ tenantKey, id })
              toast.success(SUCCESS_MESSAGES.DELETED)
            } catch (error) {
              toastApiErrorMessage(error, ERROR_MESSAGES.FAILED_TO_DELETE)
            }
          }
        })
      },
      [modal, mutation, tenantKey]
    )

    const selectedValue = useMemo(() => {
      if (!data) return null
      if (Array.isArray(value)) {
        return value.filter((val) => data.some((item) => item.id === val))
      }
      return data.some((item) => item.id === value) ? value : null
    }, [data, value])

    const options = useMemo(() => {
      const source = unselectedHistories ?? data
      return source
        ? source.map((item) => ({
            ...item,
            label: item.name || `${item.originSite} → ${item.destSite}`
          }))
        : []
    }, [data, unselectedHistories])

    return (
      <>
        <Select
          ref={ref}
          mode={mode}
          value={isLoading ? null : selectedValue}
          placeholder={placeholder}
          fieldNames={{ label: 'label', value: 'id' }}
          className={clsx(['min-w-[160px]', className])}
          options={options}
          loading={isFetching || isHistoryDetailLoading}
          onChange={handleChange}
          filterOption={(input, option) => {
            if (!option) return false

            // 検索対象のフィールドを取得
            const name = option.name?.toLowerCase() ?? ''
            const originSite = option.originSite?.toLowerCase() ?? ''
            const destSite = option.destSite?.toLowerCase() ?? ''
            const searchText = input.toLowerCase()

            // label（表示名）、originSite、destSite のいずれかに一致すればtrue
            return (
              name.includes(searchText) ||
              originSite.includes(searchText) ||
              destSite.includes(searchText)
            )
          }}
          optionRender={(option) => {
            const historyData =
              option.data as ShipmentOrderInputHistoryListEntity

            return (
              <SelectOptionWithAction label={option.label}>
                <div className="flex gap-1">
                  <Button
                    type="text"
                    size="small"
                    icon={<FaEdit />}
                    className="flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleEdit(historyData.id, historyData)
                    }}
                  >
                    {ACTIONS.EDIT}
                  </Button>
                  <Button
                    type="text"
                    size="small"
                    icon={<FaTrash />}
                    className="text-red-500 hover:bg-red-50 flex items-center gap-1"
                    onClick={(e) => {
                      e.stopPropagation()
                      handleDelete(historyData.id, option.label as string)
                    }}
                  >
                    {ACTIONS.DELETE}
                  </Button>
                </div>
              </SelectOptionWithAction>
            )
          }}
          {...props}
        />
        {modalContext}
      </>
    )
  }
)
